import { accountEntityName } from '@juvare/contact-management/backend/features/accounts/shared';
import { EntityColumns, defineColumns } from '@juvare/platform-shared/features/storage';
import { z } from 'zod';
import { backgroundCheckStatusesSchema, backgroundCheckTypeSchema } from '../../../types';

export const backgroundCheckEntityName = 'backgroundCheck';

export const backgroundCheckSchema = z.object({
    accountId: z.string().uuid(),
    requestedBy: z.string().optional(),
    status: backgroundCheckStatusesSchema,
    type: backgroundCheckTypeSchema,
    updatedAt: z.number().optional(),
    consent: z.boolean().optional(),
    internalBackgroundCheckChange: z
        .object({
            isManualChange: z.boolean().optional(),
            type: backgroundCheckTypeSchema,
            status: backgroundCheckStatusesSchema,
            updatedAt: z.number(),
        })
        .optional(),
});

export type BackgroundCheckData = z.infer<typeof backgroundCheckSchema>;

export const backgroundCheckColumns = defineColumns({
    accountId: {
        type: 'foreign-key',
        entity: 'string',
        onDeleteCascade: true,
    },
    requestedBy: {
        type: 'string',
    },
    accountStatus: {
        type: 'foreign-column',
        foreignEntity: accountEntityName,
        foreignColumn: 'systemStatus',
        foreignColumnType: 'string',
        mappedByColumn: 'accountId',
    },
    accountDisplayName: {
        type: 'foreign-column',
        foreignEntity: accountEntityName,
        foreignColumn: 'displayName',
        foreignColumnType: 'string',
        mappedByColumn: 'accountId',
    },
    accountOrgMembership: {
        type: 'foreign-column',
        foreignEntity: accountEntityName,
        foreignColumn: 'orgMembership',
        foreignColumnType: 'id-array',
        mappedByColumn: 'accountId',
    },
    accountSystemRole: {
        type: 'foreign-column',
        foreignEntity: accountEntityName,
        foreignColumn: 'systemRole',
        foreignColumnType: 'string',
        mappedByColumn: 'accountId',
    },
    status: {
        type: 'string-required',
        index: true,
    },
    type: {
        type: 'string-required',
        index: true,
    },
    updatedAt: {
        type: 'number-required',
        index: true,
    },
    consent: {
        type: 'boolean-required',
        index: true,
    },
});

export type BackgroundCheckColumns = EntityColumns<typeof backgroundCheckColumns>;
export type TypedBackgroundCheckColumns = EntityColumns<Omit<typeof backgroundCheckColumns, 'status' | 'type'>> &
    Pick<z.infer<typeof backgroundCheckSchema>, 'status' | 'type'>;
